import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import BackButton from '../components/ui-components/BackButton'
import MFHeader from '../components/MFHeader'
import MFHeaderSkeleton from '../components/skeletons/MFHeaderSkeleton'
import { MutualFundLockedOverlay } from '../components/MutualFundLockedOverlay'
import { MutualFundLockedHeroMock } from '../components/MutualFundLockedHeroMock'
import { ExecutionBar } from '../components/execution/execution-bar/ExecutionBar'
import InvestmentSection from '../features/user-investment/components/InvestmentSection'
import { MutualFundDetailLoadingLayout } from '../components/MutualFundDetailLoadingLayout'
import NoFundFound from '../components/error-states/NoFundFound'
import { useSetAppBarTheme } from '../hooks/useSetAppBarTheme'
import { useMetaDataContext } from '../state-management/contexts/MetaDataContext'
import { useMFExecutionContext } from '../state-management/contexts/MFExecutionContext'
import { goBack, lightImpactVibrate } from '../utils/appBridge'
import { getBottomPadding, getTopPadding } from '../utils/sessionStorage'
import { isWhitelistedAndNoUserInvestmentStatus } from '../utils/mf-execution/mfExecutionUtils'

export default function MutualFundLockedPage() {
  const [isin, setISIN] = useState('')
  const { isin: paramsISIN } = useParams<{ isin: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useSetAppBarTheme({ appBarTheme: 'dark' })

  useEffect(() => {
    setISIN('')
    setTimeout(() => {
      setISIN(paramsISIN ?? '')
    }, 0)
  }, [paramsISIN])

  const from = useMemo(() => searchParams?.get('from') ?? '', [searchParams])

  const handleBack = () => {
    lightImpactVibrate()
    if (from) goBack()
    else navigate(-1)
  }

  if (!isin) {
    return (
      <div className='flex h-screen w-full flex-col gap-3 pl-[19px] pr-5'>
        <div style={{ paddingTop: `${getTopPadding()}px` }} className='flex pb-4'>
          <BackButton size={24} onClick={handleBack} />
        </div>
        <MFHeaderSkeleton />
      </div>
    )
  }

  return <MutualFundLockedBody onBack={handleBack} />
}

export function MutualFundLockedBody({ onBack }: { onBack: () => void }) {
  const { data, isMutualFundDetailPageLoading, error } = useMetaDataContext()
  const {
    isActionsSheetOpen,
    isInfoSheetOpen,
    executionBarZIndex,
    executionBarConfig,
    isMFExecutionDataLoading,
    mfExecutionDataRefetch,
    mfExecutionData
  } = useMFExecutionContext()

  const isExecutionBarError = useMemo(() => {
    return isWhitelistedAndNoUserInvestmentStatus(mfExecutionData)
  }, [mfExecutionData])

  if (isMutualFundDetailPageLoading) {
    return <MutualFundDetailLoadingLayout onBack={onBack} />
  }

  if (error) {
    return (
      <div className='flex h-screen w-full flex-col gap-3 pl-[19px] pr-5'>
        <div style={{ paddingTop: `${getTopPadding()}px` }} className='flex pb-4'>
          <BackButton size={24} onClick={onBack} />
        </div>
        <div className='flex flex-1 justify-center'>
          <div className='flex h-max w-max pt-[30vh]'>
            <NoFundFound />
          </div>
        </div>
      </div>
    )
  }

  const showExecutionBar = mfExecutionData?.isWhitelisted && !mfExecutionData?.isFamilyAccess

  return (
    <div className='flex h-screen w-full flex-col overflow-hidden'>
      <div
        style={{ height: `${getTopPadding() + 8 + 40}px` }}
        className='z-[99] flex w-full shrink-0'
      >
        <div
          style={{ paddingTop: `calc(${getTopPadding() + 8}px)` }}
          className='relative flex h-full w-full items-center border-b border-white bg-white pb-5 pl-[19px] pr-5'
        >
          <div className='absolute z-[1] flex'>
            <BackButton size={24} onClick={onBack} />
          </div>
          <div className='flex w-full items-center justify-center'>
            {data?.fundDetails?.fundName ? (
              <div className='max-w-[70%] truncate font-[500]'>{data?.fundDetails?.fundName}</div>
            ) : null}
          </div>
        </div>
      </div>

      <div
        className='flex min-h-0 flex-1 flex-col overflow-y-auto no-scrollbar overflow-x-hidden overscroll-none'
        style={
          showExecutionBar ? { paddingBottom: `calc(${getBottomPadding()}px + 88px)` } : undefined
        }
      >
        <div className='w-full pt-1 text-[#232323]'>
          <div className='px-5'>
            <MFHeader />
          </div>
          <InvestmentSection />
          <div className='relative grid bg-slate-100'>
            <div className='pointer-events-none col-start-1 row-start-1 select-none blur-lg'>
              <MutualFundLockedHeroMock />
            </div>
            <div className='col-start-1 row-start-1'>
              <MutualFundLockedOverlay />
            </div>
          </div>
        </div>
      </div>

      {showExecutionBar ? (
        <div
          style={{
            zIndex: executionBarZIndex,
            pointerEvents: 'auto'
          }}
          className='fixed bottom-0 flex w-full shrink-0 bg-transparent'
        >
          <ExecutionBar
            executionBarConfig={executionBarConfig}
            isScrim={!isActionsSheetOpen && !isInfoSheetOpen}
            isError={isExecutionBarError}
            isLoading={isMFExecutionDataLoading}
            onRetry={mfExecutionDataRefetch}
          />
        </div>
      ) : null}
    </div>
  )
}
